/**
 * Was unter dem Abschnitt und unter der Grube liegt.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „die grube erkennt keinen untergrund."
 *
 * ER HAT RECHT GEHABT, UND DIE LÜCKE WAR EINE GANZE HÄLFTE. Das Abtasten lief
 * ausschliesslich über die ABSCHNITTE; eine Montagegrube entstand mit
 * `klasse: 'unbekannt'` und blieb es, bis ein Mensch die Auswahl anfasste. In
 * der Rechnung steht `unbekannt` mit 1,5 Stunden zwischen Grün (1,0) und Beton
 * (3,0) - **eine Pauschale für „weiss ich nicht" sieht in der Summe genauso
 * aus wie eine gemessene Zahl**, und die Grube ist der teurere Posten: sie
 * kostet pauschal und nicht je Meter.
 *
 * WARUM DIESE REGELN IN EINER EIGENEN DATEI STEHEN, ohne JSX daneben: eine
 * Datei mit JSX lässt sich mit `--experimental-strip-types` nicht laden, und
 * dann bliebe einem Prüfstand nur die Textsuche - **und eine Textsuche misst,
 * dass etwas dasteht, nicht, dass es stimmt.** Dieselbe Aufteilung wie bei
 * `namen.ts`, `terminraster.ts`, `klebeband.ts` und `kartenmass.ts`.
 *
 * DIE ENTSCHEIDUNG STEHT AUCH IM SERVER (`api/src/regeln/tiefbau.ts`), und das
 * ist keine Nachlässigkeit: der Browser hat die Bildpunkte, der Server hat die
 * Rechnung, und keiner von beiden kann das TypeScript des anderen laden.
 * **Prüfstand 086 legt beide Fassungen über eine Reihe von Proben
 * nebeneinander** - dieselbe Bauart wie bei den Zeitwerten aus Migration 018
 * und beim Grubenradius.
 */

/** Ein Punkt auf der Karte. Dieselbe Gestalt wie in `Tiefbaukarte.tsx`. */
export interface Ort { lat: number; lng: number; }

export interface Probe {
  /** Mittelwerte der Bildpunkte. */
  r: number; g: number; b: number;
  /** Helligkeit. */
  hell: number;
  /** Mittlere absolute Abweichung der Helligkeit: die Struktur der Fläche. */
  struktur: number;
}

export interface Vorschlag { klasse: string; sicherheit: number }

/** Aus einer Reihe von R,G,B-Werten eine Probe machen. */
export function probe(werte: readonly number[]): Probe {
  const n = werte.length / 3;
  if (!n) return { r: 0, g: 0, b: 0, hell: 0, struktur: 0 };
  let r = 0, g = 0, b = 0;
  const hell: number[] = [];
  for (let i = 0; i < werte.length; i += 3) {
    r += werte[i]; g += werte[i + 1]; b += werte[i + 2];
    hell.push((werte[i] + werte[i + 1] + werte[i + 2]) / 3);
  }
  r /= n; g /= n; b /= n;
  const mittel = hell.reduce((s, h) => s + h, 0) / n;
  const struktur = hell.reduce((s, h) => s + Math.abs(h - mittel), 0) / n;
  return { r, g, b, hell: mittel, struktur };
}

/** Grünüberschuss. Pflanzen werfen mehr Grün zurück als Rot und Blau. */
export function gruenUeberschuss(p: Probe): number {
  return 2 * p.g - p.r - p.b;
}

/**
 * Die Bezugshelligkeit: das Mittel über die HARTEN Flächen.
 *
 * Ohne sie wäre „dunkel heisst Asphalt" eine Aussage über den Sonnenstand: ein
 * Luftbild im Gegenlicht ist überall dunkler, und dann wäre alles Asphalt.
 *
 * SIE WIRD EINMAL GERECHNET, über Abschnitte UND Gruben zusammen. Zweimal
 * gerechnet bekäme eine Grube und der Abschnitt, der an ihr endet, zwei
 * verschiedene Bezugswerte - und damit zwei verschiedene Oberflächen an
 * derselben Stelle.
 */
export function bezugshelligkeit(proben: readonly Probe[]): number {
  const hart = proben.filter((p) => gruenUeberschuss(p) <= 16);
  if (!hart.length) return 128;
  return hart.reduce((s, p) => s + p.hell, 0) / hart.length;
}

/**
 * Der Entscheidungsbaum. Wörtlich der des Servers.
 *
 * Grün zuerst: der Grünüberschuss ist das eindeutigste Merkmal. Dann die
 * Struktur (Pflaster hat Fugen, Asphalt nicht), dann die Helligkeit gegen den
 * Bezug. Bleibt alles unentschieden, entscheidet die Helligkeit allein - und
 * die Sicherheit sagt ehrlich 40.
 */
export function schlageVor(p: Probe, bezug: number): Vorschlag {
  const e = gruenUeberschuss(p);
  if (e > 16) return { klasse: 'gruen', sicherheit: Math.min(99, 55 + Math.round(e)) };
  if (p.struktur > 9) {
    return { klasse: 'pflaster',
             sicherheit: Math.min(95, 45 + Math.round(p.struktur * 3)) };
  }
  if (p.hell < bezug - 4) {
    return { klasse: 'asphalt',
             sicherheit: Math.min(92, 50 + Math.round(bezug - p.hell)) };
  }
  if (p.hell > bezug + 4) {
    return { klasse: 'beton',
             sicherheit: Math.min(92, 50 + Math.round(p.hell - bezug)) };
  }
  return { klasse: p.hell < bezug ? 'asphalt' : 'beton', sicherheit: 40 };
}


/* ==========================================================================
 * WO ABGETASTET WIRD
 * ========================================================================== */

/** Neun Stützstellen entlang des Abschnitts, links und rechts also achtzehn. */
export const ABSCHNITT_STELLEN = 9;
/** Anderthalb Meter senkrecht zur Linie. So breit ist ein Graben mit Rand. */
export const ABSCHNITT_SEITLICH_METER = 1.5;

/* WIE EINE GRUBE ABGETASTET WIRD.
 *
 * Acht Punkte auf einem Ring plus die Mitte, also neun - dieselbe Zahl wie bei
 * einem Abschnitt, damit beide Proben gleich schwer wiegen.
 *
 * DER RING BLEIBT INNERHALB DER GRUBE. Sie ist rund anderthalb Meter breit
 * (`GRUBE_METER` in `Tiefbaukarte.tsx`), 0,6 Meter Radius liegen sicher darin.
 * Wer weiter aussen abtastet, misst den Rasenstreifen neben dem Gehweg und
 * schlägt Grün vor, wo Pflaster aufgenommen wird. */
export const GRUBE_PROBEN = 8;
export const GRUBE_PROBE_METER = 0.6;

/**
 * Wie viel Grad ein Meter ist - GETRENNT NACH ACHSE.
 *
 * EIN METER IST IN DER LÄNGE MEHR GRAD ALS IN DER BREITE, und zwar um den
 * Kehrwert des Kosinus: auf fünfzig Grad Nord das Anderthalbfache. Bis .99
 * stand für beide Achsen dieselbe Zahl; senkrecht zu einem Graben in
 * Ost-West-Richtung wurde damit gut zwei Meter zur Seite abgetastet statt
 * anderthalb - **und das ist bei einem Gehweg schon der Nachbar.**
 *
 * Der Boden von 0,05 ist der Riegel gegen den Pol: dort geht der Kosinus gegen
 * null, und eine Teilung dadurch ergäbe eine Unendlichkeit im Ausschnitt.
 */
export function gradJeMeter(lat: number): { lat: number; lng: number } {
  const b = 1 / 111_320;
  return { lat: b, lng: b / Math.max(0.05, Math.cos((lat * Math.PI) / 180)) };
}

/**
 * Die Stellen, an denen ein Abschnitt abgetastet wird.
 *
 * Neun Stützstellen entlang der Linie, jede einmal links und einmal rechts -
 * ein Punkt AUF der Linie träfe den Graben selbst, sobald einer offen ist.
 */
export function stellenAmAbschnitt(a: Ort, b: Ort): Ort[] {
  const grad = gradJeMeter((a.lat + b.lat) / 2);
  // Die Richtung in METERN, sonst zeigt die Senkrechte daneben.
  const dx = (b.lng - a.lng) / grad.lng, dy = (b.lat - a.lat) / grad.lat;
  const laenge = Math.hypot(dx, dy) || 1;
  const nx = (-dy / laenge) * ABSCHNITT_SEITLICH_METER * grad.lng;
  const ny = (dx / laenge) * ABSCHNITT_SEITLICH_METER * grad.lat;
  const raus: Ort[] = [];
  for (let s = 1; s <= ABSCHNITT_STELLEN; s++) {
    const t = s / (ABSCHNITT_STELLEN + 1);
    const m = { lat: a.lat + (b.lat - a.lat) * t,
                lng: a.lng + (b.lng - a.lng) * t };
    for (const seite of [1, -1]) {
      raus.push({ lat: m.lat + ny * seite, lng: m.lng + nx * seite });
    }
  }
  return raus;
}

/** Die Stellen, an denen eine Grube abgetastet wird: die Mitte und ein Ring. */
export function stellenAnDerGrube(p: Ort): Ort[] {
  const grad = gradJeMeter(p.lat);
  const raus: Ort[] = [{ lat: p.lat, lng: p.lng }];
  for (let s = 0; s < GRUBE_PROBEN; s++) {
    const w = (2 * Math.PI * s) / GRUBE_PROBEN;
    raus.push({
      lat: p.lat + Math.sin(w) * GRUBE_PROBE_METER * grad.lat,
      lng: p.lng + Math.cos(w) * GRUBE_PROBE_METER * grad.lng,
    });
  }
  return raus;
}

/**
 * Aus einer Liste von Stellen eine Probe machen.
 *
 * `leser` gibt zu einer Stelle die Bildpunkte zurück oder `null`, wenn sie
 * ausserhalb der Karte liegt. Kommt von keiner Stelle etwas, gibt es KEINE
 * Probe - und damit auch keinen Vorschlag. Eine Probe aus null Bildpunkten
 * wäre lauter Schwarz, und daraus würde brav „Asphalt" geschätzt.
 */
export function probeAn(
  stellen: readonly Ort[], leser: (p: Ort) => number[] | null,
): Probe | null {
  const werte: number[] = [];
  for (const s of stellen) {
    const w = leser(s);
    if (w) werte.push(...w);
  }
  return werte.length ? probe(werte) : null;
}
